import { Theme } from '@interfaces/global';
import { static_tips_path } from '@routes';

import { AutocompleteOption, AutocompleteRecord } from './interfaces';

export const ACCESS_ANNUAL = 3;
export const ACCESS_PRO = 4;

// Add any constants to be used throughout React App
export const WIDTH_XS = 480;
export const WIDTH_SM = 768;
export const WIDTH_MD = 992;
export const WIDTH_LG = 1200;
export const WIDTH_XL = 1350;
export const EMAIL_REGEX = /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/;
export const FULL_NAME_REGEX = /^[-'a-z]{2,}\s[-'a-z]{2,}.*$/i;
export const INVALID_NAME_REGEX =
  /^[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXZ]+(?: [bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXZ]+)*$/;
// This regex checks if full name doesn't contain vowels

export const TI_IFRAME_DOMAIN = 'https://lessin-media-company.s3.us-west-1.amazonaws.com';
export const DATAWRAPPER_IFRAME_DOMAIN = 'https://datawrapper.dwcdn.net';
export const IF_CDN_IFRAME_DOMAIN = '//if-cdn.com';
export const YOUTUBE_IFRAME_DOMAIN = 'https://www.youtube.com';
export const PARTNERSHIP_FORM_URL = 'https://ti.theinformation.com/partners';
export const PARTNERSHIP_IMAGE_URL = 'https://tii.imgix.net/global/partners/partnership-image.png';
export const SPONSORSHIPS_EMAIL_ADDRESS = 'sponsorships@theinformation.com';
export const MEDIA_KIT_URL = 'https://theinformation.docsend.com/view/z5dnmznvwgcmbdps';
export const CORPORATE_SUBSCRIPTION_FORM_URL = 'https://ti.theinformation.com/corporate';
export const CORPORATE_EMAIL_ADDRESS = 'corporate@theinformation.com';
export const DEEP_RESEARCH_HELP_EMAIL = 'deepresearch@theinformation.com';
export const EMAIL_MESSAGE_CONFIRMATION =
  'Your settings have been successfully updated. Check your inbox to verify your email.';
export const YOUNG_PROFESSIONALS_PLAN = 'yp';
export const TI_TWITTER_LINK = 'https://x.com/theinformation';
export const TI_FACEBOOK_LINK = 'https://www.facebook.com/theinformation';
export const TI_LINKEDIN_LINK = 'https://www.linkedin.com/company/the-information/';
export const TI_INSTAGRAM_LINK = 'https://www.instagram.com/theinformation/';

export const PRO_GOLD_COLOR = '#C5A26D';
export const RED_MAGENTA_COLOR = '#F32A52';

export const CARD_PAYMENT_TYPE = 'card';

// This is used to style Stripe payment element, reference: https://stripe.com/docs/elements/appearance-api
export const PAYMENT_ELEMENT_APPEARANCE = {
  variables: {
    fontFamily: '"Suisse Int’l Mono", ui-sans-serif',
    fontSizeBase: '12px',
    fontSizeSm: '12px',
    fontLineHeight: '1.3',
    fontWeightNormal: '400',
    focusBoxShadow: 'none',
    focusOutline: 'none',
    borderRadius: '0px',
    colorText: '#000000',
    colorTextPlaceholder: '#6D686E',
  },
  rules: {
    '.Input': {
      boxShadow: 'none',
      border: '1px solid #F0ECE5',
      padding: '16px 8px',
    },
    '.Label': {
      marginBottom: '8px',
      color: '#6D686E',
    },
  },
};

export const ENTERPRISE_EMAIL = 'enterprise@theinformation.com';
export const SUPPORT_EMAIL = 'support@theinformation.com';
export const CONTACT_LIST = [
  {
    title: 'For general questions or technical support:',
    email: SUPPORT_EMAIL,
  },
  {
    title: 'For information on group subscriptions for teams:',
    email: 'groups@theinformation.com',
  },
  {
    title: 'For information about sending us a tip:',
    link: static_tips_path(),
    as: 'theinformation.com/tips',
  },
  {
    title: 'For job opportunities:',
    email: 'jobs@theinformation.com',
  },
  {
    title: 'For information about upcoming events:',
    email: 'events@theinformation.com',
  },
  {
    title: 'For partnership inquiries:',
    email: 'sponsorships@theinformation.com',
  },
  {
    title: 'For members of the media interested in covering The Information:',
    email: 'press@theinformation.com',
  },
];

export const BENEFITS = [
  {
    icon: '/about/benefits/medical.svg',
    label: 'Paid Medical, Vision, & Dental',
  },
  {
    icon: '/about/benefits/time_off.svg',
    label: 'Generous Paid Time Off',
  },
  {
    icon: '/about/benefits/parental.svg',
    label: 'Parental Leave for New Parents',
  },
  {
    icon: '/about/benefits/retirement.svg',
    label: '401K Retirement Plan',
  },
  {
    icon: '/about/benefits/fitness.svg',
    label: 'Fitness Stipend',
  },
  {
    icon: '/about/benefits/phone.svg',
    label: 'Cell Phone Reimbursement',
  },
  {
    icon: '/about/benefits/commute.svg',
    label: 'Commuter Benefits',
  },
  {
    icon: '/about/benefits/spend.svg',
    label: 'Flexible Spending Account',
  },
];

// create object with same shape as AutocompleteRecord with new reference
export const BLANK_RECORD_OPTION: AutocompleteRecord = Object.create({ label: '', value: 0 });
export const BLANK_OPTION: AutocompleteOption = Object.create({ label: '', value: '' });

// Group Subscriptions
export const GROUP_SUBSCRIPTION_TEAM = 'Team Subscription';
export const GROUP_SUBSCRIPTION_GIFT = 'Gift Subscription';

// Profile post types
export const POST_TYPE_ARTICLES = 'articles';
export const POST_TYPE_FORUM_POSTS = 'forum-posts';

// Switch Field Theme colors
export const SWITCH_COLORS = {
  background: {
    off: {
      [Theme.Dark]: '#6d686e',
      [Theme.Light]: '#f0ece5',
      default: '#ffffff',
    },
    on: {
      [Theme.Gold]: '#c5a26d',
      default: '#fec6cc',
    },
  },
  handle: {
    off: {
      [Theme.Dark]: '#f0ece5',
      [Theme.Gray]: '#6d686e',
      default: '#000',
    },
    on: {
      [Theme.Gold]: '#fff',
      default: '#f32a52',
    },
  },
} as const;

export const SWITCH_BORDER_CLASSES = {
  on: {
    [Theme.Gold]: 'border border-solid border-pro-gold bg-pro-gold',
  },
  off: {
    [Theme.Gold]: 'border border-solid border-black bg-white',
    [Theme.Gray]: 'border border-solid border-grayscale-gray pointer-events-none',
  },
} as const;

export const TEXT_COLORS = {
  [Theme.Light]: 'text-grayscale-charcoal',
  [Theme.Dark]: 'text-grayscale-beige',
  [Theme.Gold]: 'text-black',
} as const;

// This is used to style react-select elements, reference: https://react-select.com/styles
export const REACT_SELECT_STYLES = {
  control: (base, { isDisabled }) => ({
    ...base,
    backgroundColor: isDisabled ? '#fff' : '',
    border: '1px solid #f0ece5',
    borderRadius: '0',
    color: isDisabled ? '#6d686e' : '#000',
    fontFamily: "'Suisse Int’l'",
    fontSize: '14px',
    fontWeight: '400',
    height: '50px',
    lineHeight: '1.3',
    padding: '15px 8px 16px',
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#000',
    cursor: 'pointer',
    ':hover': {
      color: '#f32a52',
    },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#000',
    display: 'flex',
    cursor: 'pointer',
    ':hover': {
      color: '#f32a52',
    },
  }),
  loadingMessage: (base) => ({
    ...base,
    padding: '8px',
    textAlign: 'left',
  }),
  menu: (base) => ({
    ...base,
    fontFamily: "'Suisse Int’l'",
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '1.3',
    color: '#6d686e',
    backgroundColor: '#f0ece5',
    borderRadius: 0,
    padding: '8px 0px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    ':hover': {
      color: '#fff',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    padding: '8px',
    textAlign: 'left',
  }),
  option: (base) => ({
    ...base,
    color: '#6d686e',
    backgroundColor: 'inherit',
    padding: '8px',
    ':hover': {
      color: '#fff',
      backgroundColor: '#f32a52',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6D686E',
  }),
};
